import { useCallback, useEffect, useRef, useState } from 'react';
import { EMPTY_CARD_BROWSER_FILTERS } from './useCardBrowserSearch';

const mitFarben = (colors) => (
  colors?.length ? { ...EMPTY_CARD_BROWSER_FILTERS, colors } : EMPTY_CARD_BROWSER_FILTERS
);

/**
 * Filter-Zustand einer Trefferliste, vorbelegt mit den Commander-Farben des Decks.
 *
 *   const [filters, patch] = useCardBrowserFilters(initialColors, deckId);
 *
 * `patch(partial)` führt Teiländerungen mit dem bisherigen Filter zusammen.
 *
 * Die Farben stehen schon im ersten Render (Lazy-Init aus `initialColors`): ein Effekt, der
 * sie erst NACH dem ersten Commit nachträgt, feuerte für den ersten Render eine Anfrage
 * ohne Farben unter einem eigenen Query-Key, den sonst niemand hält.
 *
 * Je Deck nur EINMAL: danach gehört der Filter dem Nutzer, der ihn abwählen darf, ohne
 * dass der Effekt ihn zurückholt. Der Effekt greift nur noch für zwei Fälle: einen
 * Deckwechsel ohne Neuaufbau (die Farben des alten Commanders blieben sonst über dem neuen
 * Deck stehen) und den Fall, dass `initialColors` erst NACH dem ersten Render eintrifft
 * (Commander-Karten laden asynchron nach) — dann trägt er die Farben einmalig nach. Der
 * Merker steht in einer Ref: er darf keinen Render auslösen, sonst liefe der Effekt ein
 * zweites Mal, bevor der Filter steht.
 *
 * Ein Aufrufer mit mehreren Trefferlisten ruft den Hook je Liste einmal auf.
 */
export function useCardBrowserFilters(initialColors, deckId) {
  const [filters, setFilters] = useState(() => mitFarben(initialColors));
  const patch = useCallback((partial) => {
    setFilters((prev) => ({ ...prev, ...partial }));
  }, []);

  const vorbelegt = useRef({ deckId, gesetzt: Boolean(initialColors?.length) });
  useEffect(() => {
    if (vorbelegt.current.deckId !== deckId) {
      vorbelegt.current = { deckId, gesetzt: false };
      setFilters((prev) => (prev.colors.length ? { ...prev, colors: [] } : prev));
    }
    if (vorbelegt.current.gesetzt || !initialColors?.length) return;
    vorbelegt.current.gesetzt = true;
    setFilters((prev) => ({ ...prev, colors: initialColors }));
  }, [deckId, initialColors]);

  return [filters, patch];
}
